import type { IAppConfigResponse } from "../types/app-config";
import {
  ENABLED_ANONYMIZATION_LAB_STORAGE_KEY,
  ENABLED_CALL_ASSISTANT_FEATURES_STORAGE_KEY,
  ENABLED_MOBILE_CHAT_SEARCH_STORAGE_KEY,
  ENABLED_WHATSAPP_BOT_STORAGE_KEY,
} from "./local-storage";

// NOTE: NSAI-1249 — the single source of truth for the client-side feature
// flags web and mobile both gate on. Not to be confused with the neighbouring
// `feature-flag-keys.ts`, which is only the obfuscation map for the /app-config
// wire format; this file describes what a flag MEANS and how it is resolved.
//
// A flag has up to three inputs, and the resolver in
// @frontend-shared/utils/feature-flags combines them:
//   1. `remoteEnabledKey` — the /app-config field that turns the feature on for
//      EVERY user. This is the shadow-drop switch: ship the code dark, flip the
//      server flag later, and the feature appears without a new app release —
//      which matters far more on mobile, where a release costs an App Store
//      review, than on web, where a redeploy costs ten minutes.
//   2. `remoteFFKey` — the /app-config field that makes the feature merely
//      AVAILABLE, without showing it to anyone. Paired with a local opt-in it
//      hands the feature to one tester on a channel where it is not public.
//   3. the per-device opt-in a developer sets by hand — DevTools on web,
//      the hidden dev-settings screen on mobile (there is no console there).
//
// Adding a flag is one entry here plus, when it needs a server switch, one
// field in the server's IAppConfigResponse. Do not add a second list.
//
// NOTE: NSAI-6224 — `remoteEnabledKey` and `remoteFFKey` are normally two
// independent server switches, and WHATSAPP_BOT is the exception worth knowing
// about: there the deployment has ONE channel switch, and its `_FF` twin makes
// the server send the FF key INSTEAD of the everyone key. Nothing changes here
// — the resolver still reads "everyone, or this device once the server allows
// it" — but do not expect to find both WhatsApp keys in one /app-config
// response.

// NOTE: The /app-config fields that carry `true` when present. Restricting the
// registry to these keeps a flag from pointing at `maxFileUploadCount` or one
// of the array/string fields, which the resolver could not interpret.
export type TAppConfigFlagKey = {
  [TKey in keyof IAppConfigResponse]-?: IAppConfigResponse[TKey] extends
    | true
    | undefined
    ? TKey
    : never;
}[keyof IAppConfigResponse];

export enum EFeatureFlag {
  CALL_ASSISTANT_FEATURES = "call_assistant_features",
  ANONYMIZATION_LAB = "anonymization_lab",
  MOBILE_CHAT_SEARCH = "mobile_chat_search",
  WHATSAPP_BOT = "whatsapp_bot",
}

// NOTE: Which apps render a surface for the flag. The mobile dev screen lists
// only its own flags, so a web-only entry never shows up there as a toggle that
// does nothing.
export enum EFeatureFlagPlatform {
  WEB = "web",
  MOBILE = "mobile",
}

export enum EFeatureFlagOverrideMode {
  // NOTE: the device opt-in can turn the feature on by itself. Correct only
  // when the feature is purely client-side visibility — the code already
  // shipped in the build and no server route has to allow it. A dev toggling
  // this on previews the feature ahead of its shadow drop.
  ALWAYS = "always",
  // NOTE: the device opt-in only counts while the server reports `remoteFFKey`.
  // Correct whenever the backend also guards the feature's endpoints: without
  // the server switch the opt-in would draw a screen whose every request 403s,
  // and — the point of the mode — a curious user who reached the dev screen
  // still cannot hand themselves a feature the deployment has not enabled.
  REQUIRES_REMOTE_FF = "requires_remote_ff",
}

export interface IFeatureFlagDefinition {
  // NOTE: also the i18n key stem — the dev screen reads its label and
  // description from `feature_flags.<key>.label` / `.description` in the
  // `devSettings` namespace. Derived rather than stored so the two cannot drift,
  // and so i18next's strict key checking resolves them at compile time.
  key: EFeatureFlag;
  // NOTE: the storage key the device opt-in is written under. Deliberately the
  // pre-existing web key: browsers that already opted in keep their opt-in, and
  // a dev reading web's localStorage and mobile's AsyncStorage sees the same
  // names.
  storageKey: string;
  overrideMode: EFeatureFlagOverrideMode;
  platforms: readonly EFeatureFlagPlatform[];
  remoteEnabledKey?: TAppConfigFlagKey;
  remoteFFKey?: TAppConfigFlagKey;
}

export const FEATURE_FLAGS: Readonly<
  Record<EFeatureFlag, IFeatureFlagDefinition>
> = {
  [EFeatureFlag.CALL_ASSISTANT_FEATURES]: {
    key: EFeatureFlag.CALL_ASSISTANT_FEATURES,
    storageKey: ENABLED_CALL_ASSISTANT_FEATURES_STORAGE_KEY,
    // NOTE: call-assistant-features.guard.ts lets the API through only when one
    // of the two server flags is set, so a device opt-in on its own would get
    // 403s rather than a working screen.
    overrideMode: EFeatureFlagOverrideMode.REQUIRES_REMOTE_FF,
    platforms: [EFeatureFlagPlatform.WEB, EFeatureFlagPlatform.MOBILE],
    remoteEnabledKey: "isCallAssistantFeaturesEnabled",
    remoteFFKey: "isCallAssistantFeaturesEnabledFF",
  },
  [EFeatureFlag.ANONYMIZATION_LAB]: {
    key: EFeatureFlag.ANONYMIZATION_LAB,
    storageKey: ENABLED_ANONYMIZATION_LAB_STORAGE_KEY,
    // NOTE: NSAI-5286 — anonymization-lab-feature.guard.ts mirrors this, and
    // both server flags already fold in the deployment-wide PII kill switch. A
    // deployment whose chats do not mask reports both as false, and no device
    // opt-in can bring the lab back: the opt-in is an opt-IN, never an override.
    overrideMode: EFeatureFlagOverrideMode.REQUIRES_REMOTE_FF,
    // NOTE: web-only for now — mobile has no Anonymization Lab screen yet. The
    // entry lives here anyway so the two clients share one list; mobile simply
    // does not render a toggle for it.
    platforms: [EFeatureFlagPlatform.WEB],
    remoteEnabledKey: "isAnonymizationLabEnabled",
    remoteFFKey: "isAnonymizationLabEnabledFF",
  },
  [EFeatureFlag.MOBILE_CHAT_SEARCH]: {
    key: EFeatureFlag.MOBILE_CHAT_SEARCH,
    storageKey: ENABLED_MOBILE_CHAT_SEARCH_STORAGE_KEY,
    // NOTE: NSAI-4401 — the search screen and its endpoints are shipped and
    // reachable; only the entry point is hidden while QA verifies the keyword
    // path. Nothing server-side refuses the request, so a dev can preview it
    // with the device opt-in alone, and the shadow drop is the server flag
    // flipping for everyone else.
    overrideMode: EFeatureFlagOverrideMode.ALWAYS,
    platforms: [EFeatureFlagPlatform.MOBILE],
    remoteEnabledKey: "isMobileChatSearchEnabled",
  },
  [EFeatureFlag.WHATSAPP_BOT]: {
    key: EFeatureFlag.WHATSAPP_BOT,
    storageKey: ENABLED_WHATSAPP_BOT_STORAGE_KEY,
    // NOTE: NSAI-6224 — the channel's endpoints (/whatsapp-bot/link-code,
    // /link-status, the Twilio webhook) are all gated on
    // IS_WHATSAPP_BOT_ENABLED server-side, so a device opt-in on its own would
    // draw a section whose every request answers 503.
    overrideMode: EFeatureFlagOverrideMode.REQUIRES_REMOTE_FF,
    platforms: [EFeatureFlagPlatform.WEB, EFeatureFlagPlatform.MOBILE],
    remoteEnabledKey: "isWhatsappBotEnabled",
    remoteFFKey: "isWhatsappBotEnabledFF",
  },
} as const;

export const FEATURE_FLAG_LIST: readonly IFeatureFlagDefinition[] =
  Object.values(FEATURE_FLAGS);
